import React from 'react';
import { TFunction } from 'i18next';
import _get from 'lodash/get';
import * as Yup from 'yup';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  WidgetsKindEnum,
} from 'graphql-common';
import { FieldItem, Values, Value } from '@lib/interfaces/form';
import { SelectOption } from '@lib/components/Select/types';
import SupportiveMessage, {
  SupportiveMessageColor,
  SupportiveMessageIconColor,
  SupportiveMessageType,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import WidgetFieldNames from '@lib/enums/fieldNames/widgetFieldNames';
import CheckBoxSingleBool from '@lib/components/CheckBoxSingleBool/CheckBoxSingleBool';
import CheckBoxAsToggle from '@lib/components/CheckBoxAsToggle/CheckBoxAsToggle';
import { CheckboxSizes } from '@lib/components/CheckBox/enums';
import QuickCreationFieldsWrapper from '../../components/QuickCreationFieldsWrapper';
import { isWidgetSelected } from './validationHelpers';

/**
 * Function type for checking user access by component
 */
type GetUserAccessByComponent = (
  component: AccessComponentEnum,
) => AccessLevelEnum;

/**
 * Original button settings from an existing widget
 */
interface OriginalButtonSettings {
  enabled?: boolean | null;
  updatedAt?: string | null;
}

/**
 * Original buttons configuration from an existing widget
 */
interface OriginalButtons {
  task?: OriginalButtonSettings | null;
  intervention?: OriginalButtonSettings | null;
  formTemplate?: OriginalButtonSettings | null;
  asset?: OriginalButtonSettings | null;
  site?: OriginalButtonSettings | null;
  teamMember?: OriginalButtonSettings | null;
}

/**
 * Button configuration for Quick Creation widget
 */
interface QuickCreationButtonConfig {
  fieldName: WidgetFieldNames;
  labelKey: string;
  accessComponent: AccessComponentEnum;
  originalButtonKey: keyof OriginalButtons;
}

/**
 * All Quick Creation button configurations with their access requirements
 */
const QUICK_CREATION_BUTTONS: QuickCreationButtonConfig[] = [
  {
    fieldName: WidgetFieldNames.QuickCreationTask,
    labelKey: 'task',
    accessComponent: AccessComponentEnum.TasksManagement,
    originalButtonKey: 'task',
  },
  {
    fieldName: WidgetFieldNames.QuickCreationIntervention,
    labelKey: 'intervention',
    accessComponent: AccessComponentEnum.InterventionsManagement,
    originalButtonKey: 'intervention',
  },
  {
    fieldName: WidgetFieldNames.QuickCreationFormTemplate,
    labelKey: 'form-template',
    accessComponent: AccessComponentEnum.TasksManagement,
    originalButtonKey: 'formTemplate',
  },
  {
    fieldName: WidgetFieldNames.QuickCreationEquipment,
    labelKey: 'asset',
    accessComponent: AccessComponentEnum.AssetsManagement,
    originalButtonKey: 'asset',
  },
  {
    fieldName: WidgetFieldNames.QuickCreationSite,
    labelKey: 'site',
    accessComponent: AccessComponentEnum.SitesManagement,
    originalButtonKey: 'site',
  },
  {
    fieldName: WidgetFieldNames.QuickCreationTeamMember,
    labelKey: 'team-member',
    accessComponent: AccessComponentEnum.UsersManagement,
    originalButtonKey: 'teamMember',
  },
];

/**
 * Checks if user has Write access to a specific component.
 * Quick Creation buttons open "create entity" pages, so Write access is required.
 */
function hasAccess(
  getUserAccessByComponent: GetUserAccessByComponent | undefined,
  accessComponent: AccessComponentEnum,
): boolean {
  if (!getUserAccessByComponent) return true;
  return getUserAccessByComponent(accessComponent) === AccessLevelEnum.Write;
}

/**
 * Determines if a button should be visible based on access and original state
 *
 * Rules:
 * - Create mode: Only show buttons user has access to
 * - Edit mode: Show if user has access OR if button was previously enabled
 */
function shouldShowButton(
  config: QuickCreationButtonConfig,
  getUserAccessByComponent: GetUserAccessByComponent | undefined,
  isEditMode: boolean,
  originalButtons: OriginalButtons | undefined,
): boolean {
  const userHasAccess = hasAccess(
    getUserAccessByComponent,
    config.accessComponent,
  );

  if (!isEditMode) {
    // Create mode: only show if user has access
    return userHasAccess;
  }

  // Edit mode: show if user has access OR button was previously enabled
  const originalButton = originalButtons?.[config.originalButtonKey];
  const wasEnabled = originalButton?.enabled === true;

  return userHasAccess || wasEnabled;
}

/**
 * Determines if a button should be disabled
 *
 * A button is disabled in edit mode when:
 * - User doesn't have access to it
 * - But it was previously enabled (so we show it but disable it)
 */
function shouldDisableButton(
  config: QuickCreationButtonConfig,
  getUserAccessByComponent: GetUserAccessByComponent | undefined,
): boolean {
  return !hasAccess(getUserAccessByComponent, config.accessComponent);
}

/**
 * Validation for Quick Creation toggles - at least one visible button must be selected
 */
function getQuickCreationTogglesValidation(
  t: TFunction,
  visibleFieldNames: WidgetFieldNames[],
) {
  return Yup.boolean().when(WidgetFieldNames.Widget, {
    is: (widget: SelectOption | undefined) =>
      _get(widget, 'data.kind') === WidgetsKindEnum.QuickCreation,
    then: () =>
      Yup.boolean().test(
        'at-least-one-selected',
        t('at-least-one-button-required'),
        function validateAtLeastOne() {
          const { parent } = this;

          // Check only visible buttons
          return visibleFieldNames.some((fieldName) =>
            Boolean(_get(parent, fieldName)),
          );
        },
      ),
    otherwise: (schema) => schema.notRequired(),
  });
}

/**
 * Creates a toggle field for Quick Creation widget
 */
function createQuickCreationToggle(
  name: WidgetFieldNames,
  label: string,
  t: TFunction,
  options: {
    isLastField?: boolean;
    visibleFieldNames?: WidgetFieldNames[];
    /** Static disabled state - always disabled regardless of value */
    isDisabled?: boolean;
    /** Dynamic disabled function - can check current value to decide */
    isDisabledFn?: (values: Values) => boolean;
  },
): FieldItem {
  const {
    isLastField,
    visibleFieldNames = [],
    isDisabled,
    isDisabledFn,
  } = options;

  // Determine the disabled function
  let disabledFn: ((values: Values, fieldName?: string) => boolean) | undefined;
  if (isDisabledFn) {
    disabledFn = isDisabledFn;
  } else if (isDisabled) {
    disabledFn = () => true;
  }

  return {
    name,
    label,
    element: CheckBoxSingleBool,
    // Only add validation to the last field to show error once
    validation: isLastField
      ? getQuickCreationTogglesValidation(t, visibleFieldNames)
      : undefined,
    componentProps: {
      CheckBoxComponent: CheckBoxAsToggle,
      options: [{ label: 'checked', value: 'checked' }],
      size: CheckboxSizes.Small,
    },
    hideErrorsSpace: true,
    isOptionalFn() {
      return false;
    },
    // Disable the toggle based on access or dynamic function
    isDisabledFn: disabledFn,
  };
}

/**
 * Options for getQuickCreationFields
 */
export interface GetQuickCreationFieldsOptions {
  /** Function to check user access by component */
  getUserAccessByComponent?: GetUserAccessByComponent;
  /** Function to check company-level access (plan restrictions) */
  getUserAccessByCompany?: GetUserAccessByComponent;
  /** Whether this is edit mode (vs create mode) */
  isEditMode?: boolean;
  /** Original buttons configuration from the widget being edited */
  originalButtons?: OriginalButtons;
}

/**
 * Returns fields for Quick Creation widget
 *
 * Access control logic:
 * - Create mode: Only show buttons user has access to
 * - Edit mode: Show buttons user has access to OR buttons that were previously enabled
 *   - If showing a button user doesn't have access to, it will be disabled
 *
 * Company-level (plan) restrictions for intervention:
 * - If intervention is locked by company plan:
 *   - In create mode: button is hidden
 *   - In edit mode with button currently enabled: can disable but not re-enable
 *   - In edit mode with button currently disabled: toggle is disabled
 */
export default function getQuickCreationFields(
  t: TFunction,
  options?: GetQuickCreationFieldsOptions,
): FieldItem[] {
  const {
    getUserAccessByComponent,
    getUserAccessByCompany,
    isEditMode = false,
    originalButtons,
  } = options || {};

  // Check if intervention is locked at the company (plan) level
  const isInterventionLockedByPlan = getUserAccessByCompany
    ? getUserAccessByCompany(AccessComponentEnum.InterventionsManagement) ===
      AccessLevelEnum.LimitedRead
    : false;

  // Filter buttons based on access and original state
  const visibleButtons = QUICK_CREATION_BUTTONS.filter((config) => {
    // For intervention, also check company-level access
    if (
      config.originalButtonKey === 'intervention' &&
      isInterventionLockedByPlan
    ) {
      // In create mode, hide the button if locked by plan
      if (!isEditMode) return false;
      // In edit mode, show if it was previously enabled (so user can disable it)
      const wasEnabled = originalButtons?.intervention?.enabled === true;
      return wasEnabled;
    }

    return shouldShowButton(
      config,
      getUserAccessByComponent,
      isEditMode,
      originalButtons,
    );
  });

  // Collect visible field names for validation
  const visibleFieldNames = visibleButtons.map((config) => config.fieldName);

  // Create toggle fields for visible buttons
  const toggleFields = visibleButtons.map((config, index) => {
    const isLastField = index === visibleButtons.length - 1;
    const isDisabledByUserAccess = shouldDisableButton(
      config,
      getUserAccessByComponent,
    );

    // Special handling for intervention locked by company plan
    if (
      config.originalButtonKey === 'intervention' &&
      isInterventionLockedByPlan
    ) {
      // Use dynamic function: disable only when current value is false (can't enable)
      // Allow disabling when current value is true (can disable)
      return createQuickCreationToggle(
        config.fieldName,
        t(config.labelKey),
        t,
        {
          isLastField,
          visibleFieldNames,
          isDisabledFn: (values: Values) => {
            const currentValue = _get(values, config.fieldName);
            // If currently enabled (true), allow toggle (so user can disable)
            // If currently disabled (false), disable toggle (can't enable)
            return !currentValue;
          },
        },
      );
    }

    return createQuickCreationToggle(config.fieldName, t(config.labelKey), t, {
      isLastField,
      visibleFieldNames,
      isDisabled: isDisabledByUserAccess,
    });
  });

  return [
    {
      name: WidgetFieldNames.QuickCreationInfo,
      customRender: () => (
        <SupportiveMessage
          text={t('home.widget-quick-creation-info')}
          leftIcon="info"
          color={SupportiveMessageColor.Gray}
          iconColor={SupportiveMessageIconColor.Inner}
          type={SupportiveMessageType.Transparent}
          hasBottomSpace={false}
        />
      ),
      isVisibleFn: (values: Values) =>
        isWidgetSelected(values, WidgetsKindEnum.QuickCreation),
    },
    {
      name: WidgetFieldNames.QuickCreationToggles,
      element: QuickCreationFieldsWrapper,
      componentProps: {
        title: t('select-buttons-to-display'),
      },
      isVisibleFn: (values: Values) =>
        isWidgetSelected(values, WidgetsKindEnum.QuickCreation),
      groupFields: toggleFields,
    },
  ];
}
